import React, { memo } from "react";
import {
  DeleteIcon,
  RepeatClockIcon,
  TriangleUpIcon,
  TriangleDownIcon,
  DragHandleIcon,
} from "@chakra-ui/icons";
import { Box } from "@chakra-ui/layout";
import { HStack, IconButton, SkeletonText } from "@chakra-ui/react";
import { Table, Tbody, Td, Th, Tr, Thead } from "@chakra-ui/table";
import { Tag } from "@chakra-ui/tag";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import DateUtil from "../../util/DateUtil";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export type SortColumn =
  | "order"
  | "fieldName"
  | "owner"
  | "fieldType"
  | "version"
  | "created"
  | "lastModified";
export type SortDirection = "asc" | "desc";

interface ExtendedFieldsTableProps {
  fields: CherryHubAdminApi.ExtendedFieldDefinition[] | null;
  isLoading: boolean;
  debouncedQuery: string;
  disableActions?: boolean;
  sortColumn: SortColumn | null;
  sortDirection: SortDirection;
  isOrderingMode: boolean;
  onClickEditField: (field: CherryHubAdminApi.ExtendedFieldDefinition) => void;
  onClickDeleteField: (
    field: CherryHubAdminApi.ExtendedFieldDefinition
  ) => void;
  onClickRestoreField: (
    field: CherryHubAdminApi.ExtendedFieldDefinition
  ) => void;
  onSortChange: (column: SortColumn) => void;
  onReorderField: (fieldId: string, newIndex: number) => void;
}

interface SortableRowProps {
  field: CherryHubAdminApi.ExtendedFieldDefinition;
  disableActions?: boolean;
  isOrderingMode: boolean;
  onClickEditField: (field: CherryHubAdminApi.ExtendedFieldDefinition) => void;
  onClickDeleteField: (
    field: CherryHubAdminApi.ExtendedFieldDefinition
  ) => void;
  onClickRestoreField: (
    field: CherryHubAdminApi.ExtendedFieldDefinition
  ) => void;
  getFieldTypeLabel: (
    fieldType?: CherryHubAdminApi.ExtendedFieldType
  ) => string;
}

const SortableRow: React.FC<SortableRowProps> = ({
  field,
  disableActions,
  isOrderingMode,
  onClickEditField,
  onClickDeleteField,
  onClickRestoreField,
  getFieldTypeLabel,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Tr ref={setNodeRef} style={style}>
      <Td width="0.1%" whiteSpace="nowrap">
        <HStack spacing={1}>
          {isOrderingMode && (
            <Box
              {...attributes}
              {...listeners}
              cursor="grab"
              _active={{ cursor: "grabbing" }}
              p={1}
            >
              <DragHandleIcon color="gray.400" />
            </Box>
          )}
          {!isOrderingMode && (
            <Box minWidth="30px" textAlign="center">
              {field.FieldOrder ?? "—"}
            </Box>
          )}
        </HStack>
      </Td>
      <Td
        title={isOrderingMode ? "" : "Edit extended field."}
        _hover={
          !field.Meta?.IsDeleted && !isOrderingMode
            ? { textDecoration: "underline", cursor: "pointer" }
            : {}
        }
        onClick={() =>
          !field.Meta?.IsDeleted && !isOrderingMode && onClickEditField(field)
        }
      >
        {!!field.Meta?.IsDeleted && (
          <Tag size="sm" mr="2">
            Deleted
          </Tag>
        )}
        {field.FieldName}
      </Td>
      <Td width="0.1%" whiteSpace="nowrap">
        {field.Owner || "—"}
      </Td>
      <Td width="0.1%" whiteSpace="nowrap">
        {getFieldTypeLabel(field.FieldType)}
      </Td>
      <Td width="0.1%" whiteSpace="nowrap" textAlign="center">
        {(field as any).Version || "—"}
      </Td>
      <Td width="0.1%" whiteSpace="nowrap">
        {DateUtil.getLocalDateFormatFromString(field.Meta?.DateCreated)}
      </Td>
      <Td width="0.1%" whiteSpace="nowrap">
        {DateUtil.getLocalDateFormatFromString(field.Meta?.DateUpdated)}
      </Td>
      <Td width="0.1%" whiteSpace="nowrap">
        <HStack spacing={2} justifyContent="center">
          {field.Meta?.IsDeleted ? (
            <IconButton
              aria-label="Restore extended field."
              title="Restore extended field."
              icon={<RepeatClockIcon />}
              size="xs"
              isDisabled={disableActions || isOrderingMode}
              onClick={() => onClickRestoreField(field)}
            />
          ) : (
            <IconButton
              marginLeft="1"
              aria-label="Delete extended field."
              title="Delete extended field."
              icon={<DeleteIcon />}
              size="xs"
              isDisabled={disableActions || isOrderingMode}
              onClick={() => onClickDeleteField(field)}
            />
          )}
        </HStack>
      </Td>
    </Tr>
  );
};

export const ExtendedFieldsTable = memo(
  ({
    fields,
    isLoading,
    disableActions,
    debouncedQuery,
    sortColumn,
    sortDirection,
    isOrderingMode,
    onClickEditField,
    onClickDeleteField,
    onClickRestoreField,
    onSortChange,
    onReorderField,
  }: ExtendedFieldsTableProps) => {
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id && fields) {
        const newIndex = fields.findIndex((f) => f.id === over.id);
        onReorderField(active.id as string, newIndex);
      }
    };
    const getFieldTypeLabel = (
      fieldType?: CherryHubAdminApi.ExtendedFieldType
    ) => {
      switch (fieldType) {
        case "Text":
          return "Text Input";
        case "TextArea":
          return "Text Area";
        case "TextSelection":
          return "Text Selection";
        case "TextMultiSelection":
          return "Text Multi-Selection";
        case "Number":
          return "Number Input";
        case "NumberSelection":
          return "Number Selection";
        case "NumberMultiSelection":
          return "Number Multi-Selection";
        case "Date":
          return "Date";
        case "DateTime":
          return "Date & Time";
        case "Time":
          return "Time";
        case "Toggle":
          return "Toggle";
        default:
          return fieldType || "Unknown";
      }
    };

    const SortableHeader = ({
      column,
      children,
    }: {
      column: SortColumn;
      children: React.ReactNode;
    }) => {
      const isActive = sortColumn === column;
      const Icon = sortDirection === "asc" ? TriangleUpIcon : TriangleDownIcon;

      return (
        <Th
          cursor={isOrderingMode ? "default" : "pointer"}
          onClick={() => !isOrderingMode && onSortChange(column)}
          _hover={isOrderingMode ? {} : { backgroundColor: "gray.50" }}
          userSelect="none"
          opacity={isOrderingMode ? 0.6 : 1}
        >
          <HStack spacing={1}>
            <Box>{children}</Box>
            {isActive && <Icon boxSize={3} />}
          </HStack>
        </Th>
      );
    };

    const isDragDisabled = !isOrderingMode;

    return (
      <Box w="100%" overflowX="auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Table size="sm">
            <Thead>
              <Tr>
                <SortableHeader column="order">Order</SortableHeader>
                <SortableHeader column="fieldName">Field Name</SortableHeader>
                <SortableHeader column="owner">Owner</SortableHeader>
                <SortableHeader column="fieldType">Field Type</SortableHeader>
                <SortableHeader column="version">Version</SortableHeader>
                <SortableHeader column="created">Created</SortableHeader>
                <SortableHeader column="lastModified">
                  Last Modified
                </SortableHeader>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {!isLoading && fields && (
                <SortableContext
                  items={fields.map((f) => f.id)}
                  strategy={verticalListSortingStrategy}
                  disabled={isDragDisabled}
                >
                  {fields.map((field) => (
                    <SortableRow
                      key={field.id}
                      field={field}
                      disableActions={disableActions}
                      isOrderingMode={isOrderingMode}
                      onClickEditField={onClickEditField}
                      onClickDeleteField={onClickDeleteField}
                      onClickRestoreField={onClickRestoreField}
                      getFieldTypeLabel={getFieldTypeLabel}
                    />
                  ))}
                </SortableContext>
              )}
              {fields?.length === 0 && !isLoading && (
                <Tr>
                  <Td colSpan={8} textAlign="center">
                    No extended fields found matching query '{debouncedQuery}'
                  </Td>
                </Tr>
              )}
              {isLoading && (
                <Tr>
                  <Td width="20%">
                    <SkeletonText noOfLines={12} spacing="4" width="100%" />
                  </Td>
                  <Td width="20%">
                    <SkeletonText noOfLines={12} spacing="4" width="100%" />
                  </Td>
                  <Td width="10%" whiteSpace="nowrap">
                    <SkeletonText noOfLines={12} spacing="4" width="100%" />
                  </Td>
                  <Td width="10%" whiteSpace="nowrap">
                    <SkeletonText noOfLines={12} spacing="4" width="100%" />
                  </Td>
                  <Td width="10%" whiteSpace="nowrap">
                    <SkeletonText noOfLines={12} spacing="4" width="100%" />
                  </Td>
                  <Td width="10%" whiteSpace="nowrap">
                    <SkeletonText noOfLines={12} spacing="4" width="100%" />
                  </Td>
                  <Td width="10%" whiteSpace="nowrap">
                    <SkeletonText noOfLines={12} spacing="4" width="100%" />
                  </Td>
                  <Td width="10%" whiteSpace="nowrap">
                    <SkeletonText noOfLines={12} spacing="4" width="100%" />
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </DndContext>
      </Box>
    );
  }
);
